import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '@/hooks/useSiteSettings';

export default function HeroSection() {
  const { t } = useTranslation('home');
  const { settings, loading } = useSiteSettings();
  const [mounted, setMounted] = useState(false);
  const [current, setCurrent] = useState(0);
  const [firstImageReady, setFirstImageReady] = useState(false);
  const [preloaded, setPreloaded] = useState<Set<number>>(new Set());

  // Collect the 4 hero image slots (h1..h4), skipping empty ones.
  const heroImages = [
    settings.homeHeroImages.h1,
    settings.homeHeroImages.h2,
    settings.homeHeroImages.h3,
    settings.homeHeroImages.h4,
  ].filter(Boolean) as string[];

  // Fallback to the legacy single hero image if no slots are set.
  const images = heroImages.length > 0
    ? heroImages
    : settings.homeHeroImage
      ? [settings.homeHeroImage]
      : [];

  useEffect(() => {
    setMounted(true);
  }, []);

  // Preload all images and track when the first one is ready
  useEffect(() => {
    if (images.length === 0) {
      setFirstImageReady(true);
      return;
    }

    let cancelled = false;
    const loadedIndices = new Set<number>();

    images.forEach((url, index) => {
      const img = new Image();
      img.onload = () => {
        if (cancelled) return;
        loadedIndices.add(index);
        setPreloaded((prev) => new Set([...prev, index]));
        if (index === 0) {
          setFirstImageReady(true);
        }
      };
      img.onerror = () => {
        if (cancelled) return;
        loadedIndices.add(index); // treat error as "done" so we don't block forever
        setPreloaded((prev) => new Set([...prev, index]));
        if (index === 0) {
          setFirstImageReady(true);
        }
      };
      img.src = url;
    });

    return () => {
      cancelled = true;
    };
  }, [images.join(',')]);

  const goTo = useCallback(
    (index: number) => {
      if (images.length <= 1) return;
      setCurrent((index + images.length) % images.length);
    },
    [images.length],
  );

  // Auto-advance the carousel.
  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(() => {
      setCurrent((c) => (c + 1) % images.length);
    }, 5000);
    return () => clearInterval(id);
  }, [images.length]);

  const hasImages = images.length > 0;

  return (
    <section className="relative w-full overflow-hidden">
      {/* Full-screen loader until settings load AND the first hero image is ready */}
      {(loading || (hasImages && !firstImageReady)) && (
        <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-white">
          <div className="w-10 h-10 rounded-full border-2 border-ink-100 border-t-warm-500 animate-spin" />
          <p className="mt-4 text-sm text-ink-400">{t('hero.loading') || 'Loading...'}</p>
        </div>
      )}

      {/* Background: only shown once settings are loaded and no images are configured */}
      <div
        className={`absolute inset-0 bg-gradient-to-b from-ink-900 via-ink-800 to-ink-700 transition-opacity duration-700 ${
          loading || hasImages ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {/* Images layer */}
      {hasImages && (
        <div className="absolute inset-0">
          {images.map((url, index) => (
            <img
              key={url}
              src={url}
              alt={`Cosmetic packaging hero ${index + 1}`}
              className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 ${
                index === current ? 'opacity-100' : 'opacity-0'
              } ${preloaded.has(index) ? '' : 'opacity-0'}`}
            />
          ))}
        </div>
      )}

      {/* Dark overlay for text contrast — only when images are visible */}
      {hasImages && firstImageReady && (
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/40" />
      )}

      {/* Subtle geometric pattern overlay */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-warm-500/40 to-transparent" />

      <div className="relative w-full px-4 md:px-6 lg:px-10 pt-12 md:pt-16 lg:pt-24 pb-20 md:pb-28 lg:pb-32">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm rounded-full px-4 py-2 border border-white/10 mb-6 md:mb-8 transition-all duration-700 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="w-1.5 h-1.5 rounded-full bg-warm-500" />
            <span className="text-xs md:text-sm font-medium text-white/70 tracking-wide uppercase">
              {t('hero.badge') || 'Global Cosmetic Packaging Sourcing'}
            </span>
          </div>

          {/* Title */}
          <h1
            className={`text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-5 md:mb-6 transition-all duration-700 delay-100 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {t('hero.title') || 'Your Global Partner in\nCosmetic Packaging'}
          </h1>

          {/* Quick links */}
          <div
            className={`flex items-center justify-center gap-4 md:gap-6 transition-all duration-700 delay-200 ${
              mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <span className="text-xs text-white/30 uppercase tracking-wider">{t('hero.quickLinks') || 'Popular'}</span>
            {['Glass Bottles', 'Airless Pumps', 'Jars', 'Tubes'].map((tag) => (
              <a
                key={tag}
                href={`/packaging?search=${encodeURIComponent(tag)}`}
                className="text-xs md:text-sm text-white/40 hover:text-warm-400 transition-colors"
              >
                {tag}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Carousel arrows */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => goTo(current - 1)}
            aria-label="Previous slide"
            className="absolute left-4 md:left-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <i className="ri-arrow-left-s-line text-xl md:text-2xl" />
          </button>
          <button
            type="button"
            onClick={() => goTo(current + 1)}
            aria-label="Next slide"
            className="absolute right-4 md:right-6 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <i className="ri-arrow-right-s-line text-xl md:text-2xl" />
          </button>
        </>
      )}

      {/* Dots */}
      {images.length > 1 && (
        <div className="absolute bottom-6 md:bottom-8 left-0 right-0 z-10 flex items-center justify-center gap-2.5">
          {images.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => goTo(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === current ? 'w-7 bg-warm-500' : 'w-2 bg-white/40 hover:bg-white/70'
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}